// Aba "Atividades" — visão Kanban (drag-drop nativo HTML5).
// Wave 2 (Antd) — 2026-06-01. Mantém drag-drop manual (HTML5) por ser leve.

import { useState } from 'react';
import { Card, Typography, Badge, Space, Avatar, Tooltip } from 'antd';
import type { Atividade } from './types';
import { STATUS_ATIVIDADE, PRIORIDADE_ATIVIDADE, TIPO_ATIVIDADE } from './types';
import { fmtData } from './utils';
import { BarraProgresso } from './shared-components';

export function KanbanAtividades({ atividades, onEditar, onMoverStatus }: {
  atividades: Atividade[];
  onEditar: (a: Atividade) => void;
  onMoverStatus: (id: string, status: string) => void;
}) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<string | null>(null);
  const COLS_ORDEM = ['pendente', 'em_andamento', 'bloqueada', 'concluida', 'cancelada'];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${COLS_ORDEM.length}, 1fr)`,
      gap: 10, alignItems: 'start' }}>
      {COLS_ORDEM.map(colKey => {
        const v = STATUS_ATIVIDADE[colKey];
        const cards = atividades.filter(a => a.status === colKey);
        const isOver = overCol === colKey;
        return (
          <div key={colKey}
            onDragOver={e => { e.preventDefault(); setOverCol(colKey); }}
            onDragLeave={() => setOverCol(o => o === colKey ? null : o)}
            onDrop={e => {
              e.preventDefault();
              setOverCol(null);
              const id = e.dataTransfer.getData('text/plain') || draggingId;
              if (id) {
                const ativ = atividades.find(a => a.id === id);
                if (ativ && ativ.status !== colKey) onMoverStatus(id, colKey);
              }
              setDraggingId(null);
            }}
            style={{
              background: '#FAFBFF', borderRadius: 6, padding: 8, minHeight: 200,
              border: isOver ? `2px dashed ${v.cor}` : '0.5px solid #e0e0e0',
              transition: 'border 0.15s',
            }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '4px 6px', marginBottom: 8 }}>
              <Typography.Text strong style={{ fontSize: 11, color: v.cor,
                textTransform: 'uppercase', letterSpacing: 0.4 }}>{v.label}</Typography.Text>
              <Badge count={cards.length} showZero
                style={{ backgroundColor: '#fff', color: '#888', borderColor: '#ddd' }} />
            </div>
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {cards.map(a => {
                const pr = PRIORIDADE_ATIVIDADE[a.prioridade] || PRIORIDADE_ATIVIDADE.media;
                const tp = a.tipo ? TIPO_ATIVIDADE[a.tipo] : null;
                const atrasada = a.data_termino_previsto
                  && new Date(a.data_termino_previsto) < new Date()
                  && a.status !== 'concluida' && a.status !== 'cancelada';
                return (
                  <Card key={a.id} size="small" hoverable
                    draggable
                    onDragStart={e => {
                      e.dataTransfer.setData('text/plain', a.id);
                      e.dataTransfer.effectAllowed = 'move';
                      setDraggingId(a.id);
                    }}
                    onDragEnd={() => setDraggingId(null)}
                    onClick={() => onEditar(a)}
                    styles={{ body: { padding: 9 } }}
                    style={{
                      cursor: 'grab',
                      opacity: draggingId === a.id ? 0.4 : 1,
                    }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: 13, marginTop: 1 }}>{tp?.emoji || '📌'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Typography.Text strong style={{ fontSize: 12, lineHeight: 1.3 }}>
                          {a.titulo}
                        </Typography.Text>
                        {a.atribuicoes && a.atribuicoes.length > 0 && (
                          <Avatar.Group size="small" max={{ count: 3 }} style={{ marginTop: 5 }}>
                            {a.atribuicoes.map(x => (
                              <Tooltip key={x.email} title={x.email}>
                                <Avatar size={18} style={{ background: '#0930D0', fontSize: 9, fontWeight: 600 }}>
                                  {x.email.charAt(0).toUpperCase()}
                                </Avatar>
                              </Tooltip>
                            ))}
                          </Avatar.Group>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', marginTop: 6, gap: 4 }}>
                          <Typography.Text style={{ fontSize: 10, color: atrasada ? '#B91626' : '#888' }}>
                            {a.data_termino_previsto ? fmtData(a.data_termino_previsto) : '—'}
                            {atrasada && ' ⚠'}
                          </Typography.Text>
                          <Tooltip title={pr.label}><span style={{ fontSize: 10 }}>{pr.emoji}</span></Tooltip>
                        </div>
                        {(a.progresso_pct || 0) > 0 && (
                          <div style={{ marginTop: 4 }}>
                            <BarraProgresso pct={a.progresso_pct} />
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
              {cards.length === 0 && (
                <Typography.Text type="secondary" italic style={{ fontSize: 10, textAlign: 'center', display: 'block', padding: 10 }}>
                  Arraste cards pra cá
                </Typography.Text>
              )}
            </Space>
          </div>
        );
      })}
    </div>
  );
}
